import { useEffect, useState } from "react";
import api from "@/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Pencil, Trash2, Plane, Building2, Ticket, Bus, Users2, Package } from "lucide-react";
import { toast } from "sonner";

const emptySupplier = {
  name: "", type: "hotel", contact_person: "", phone: "", email: "", country: "", city: "", notes: "",
};

const typeIcons = {
  airline: Plane, hotel: Building2, visa: Ticket, transport: Bus, ground_service: Users2, other: Package,
};

const typeBadge = {
  airline: "bg-sky-50 text-sky-700",
  hotel: "bg-emerald-50 text-emerald-700",
  visa: "bg-violet-50 text-violet-700",
  transport: "bg-amber-50 text-amber-700",
  ground_service: "bg-rose-50 text-rose-700",
  other: "bg-slate-100 text-slate-700",
};

export default function Suppliers() {
  const { t } = useI18n();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptySupplier);

  const load = async () => {
    const { data } = await api.get("/suppliers");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(emptySupplier); setOpen(true); };
  const openEdit = (s) => { setEditing(s); setForm({ ...emptySupplier, ...s }); setOpen(true); };

  const save = async () => {
    try {
      if (editing) await api.put(`/suppliers/${editing.id}`, form);
      else await api.post("/suppliers", form);
      toast.success(t("saved"));
      setOpen(false);
      load();
    } catch { toast.error("Error"); }
  };

  const del = async (id) => {
    if (!window.confirm(t("confirmDelete"))) return;
    try {
      await api.delete(`/suppliers/${id}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  const typeKey = (tp) => ({
    airline: "typeAirline", hotel: "typeHotel", visa: "typeVisa",
    transport: "typeTransport", ground_service: "typeGroundService", other: "typeOther",
  }[tp] || "typeOther");

  return (
    <div className="space-y-6" data-testid="suppliers-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="uppercase-label">{t("suppliers")}</div>
          <h1 className="display-font text-3xl font-bold text-[hsl(var(--primary))] mt-1">{t("suppliers")}</h1>
          <div className="mt-3 divider-accent" />
        </div>
        <Button onClick={openNew} data-testid="supplier-add-btn" className="btn-accent">
          <Plus size={16} className="me-2" /> {t("newSupplier")}
        </Button>
      </div>

      <div className="dashboard-card">
        {items.length === 0 ? (
          <div className="text-center py-12 text-sm text-[hsl(var(--muted-foreground))]">{t("empty")}</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("supplierName")}</TableHead>
                <TableHead>{t("supplierType")}</TableHead>
                <TableHead>{t("contactPerson")}</TableHead>
                <TableHead>{t("phone")}</TableHead>
                <TableHead>{t("email")}</TableHead>
                <TableHead>{t("city")}</TableHead>
                <TableHead className="text-end">{t("actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((s) => {
                const Icon = typeIcons[s.type] || Package;
                return (
                  <TableRow key={s.id} data-testid={`supplier-row-${s.id}`}>
                    <TableCell className="font-semibold">{s.name}</TableCell>
                    <TableCell>
                      <span className={"inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs " + (typeBadge[s.type] || typeBadge.other)}>
                        <Icon size={12} />
                        {t(typeKey(s.type))}
                      </span>
                    </TableCell>
                    <TableCell>{s.contact_person || "—"}</TableCell>
                    <TableCell>{s.phone || "—"}</TableCell>
                    <TableCell>{s.email || "—"}</TableCell>
                    <TableCell>{[s.city, s.country].filter(Boolean).join(" · ") || "—"}</TableCell>
                    <TableCell className="text-end space-x-1 rtl:space-x-reverse">
                      <Button size="icon" variant="ghost" onClick={() => openEdit(s)} data-testid={`supplier-edit-${s.id}`}>
                        <Pencil size={15} />
                      </Button>
                      {isAdmin && (
                        <Button size="icon" variant="ghost" onClick={() => del(s.id)} className="text-red-600" data-testid={`supplier-del-${s.id}`}>
                          <Trash2 size={15} />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editing ? t("editSupplier") : t("addSupplier")}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label={t("supplierName")}><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="supplier-name-input" /></Field>
            <Field label={t("supplierType")}>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="supplier-type-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="airline">{t("typeAirline")}</SelectItem>
                  <SelectItem value="hotel">{t("typeHotel")}</SelectItem>
                  <SelectItem value="visa">{t("typeVisa")}</SelectItem>
                  <SelectItem value="transport">{t("typeTransport")}</SelectItem>
                  <SelectItem value="ground_service">{t("typeGroundService")}</SelectItem>
                  <SelectItem value="other">{t("typeOther")}</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label={t("contactPerson")}><Input value={form.contact_person} onChange={(e) => setForm({ ...form, contact_person: e.target.value })} /></Field>
            <Field label={t("phone")}><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Field>
            <Field label={t("email")}><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
            <Field label={t("country")}><Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} /></Field>
            <Field label={t("city")}><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></Field>
            <div className="sm:col-span-2">
              <Field label={t("notes")}><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{t("cancel")}</Button>
            <Button onClick={save} data-testid="supplier-save-btn" className="bg-[hsl(var(--primary))] hover:opacity-90 text-white">{t("save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-xs uppercase-label">{label}</Label>
    {children}
  </div>
);
